// FlightsList.js
import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Image,
  Text,
  VStack,
  HStack,
  IconButton,
  Spacer,
  useColorMode,
  useToast,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from "@chakra-ui/react";
import { CloseIcon, ChevronDownIcon } from "@chakra-ui/icons";
import { FaTrash, FaClock, FaPlane } from "react-icons/fa";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import axiosInstance from "../../helpers/axiosInstance";
import GridIcon from "../../assets/icons/dot_grid_icon.svg";
import { useCurrency } from "../CurrencyExchange/CurrencyContext";
import Translate from "../../components/LanguageTranslation/Translate";
import ExternalLinkButton from "../ExternalLinkButton";
import AddFlightItemModal from "./AddFlightItemModal";

const FlightsList = ({
  flights, // [{ route: 'NYC ➔ AMS', flights:[...] }, ...]
  setFlights, // allows parent to stay in-sync
  handleFlightDelete, // callback supplied by parent
  tripId,
  setView,
  handleSuggestionClick,
  isAddFlightOpen,
  setIsAddFlightOpen,
  city,
}) => {
  // console.log("flights", flights);
  const { colorMode } = useColorMode();
  const toast = useToast();
  const { convertCurrency, currency } = useCurrency();
  const [hoveredItem, setHoveredItem] = useState(null);
  // --- internal state mirrors parent flights prop -------------------------
  const [routeData, setRouteData] = useState(flights ?? []);

  // keep local copy fresh if parent updates
  useEffect(() => {
    setRouteData(flights ?? []);
  }, [flights]);

  // Function to format route with plane icon
  const formatRoute = (route) => {
    if (!route) return "";
    if (route.includes("--> ")) {
      const parts = route.split("--> ");
      return (
        <HStack spacing={2} display="inline-flex">
          <Text mb={0}>{parts[0]}</Text>
          <FaPlane />
          <Text mb={0}>{parts[1]}</Text>
        </HStack>
      );
    }
    return route;
  };

  // ------------------------------------------------------------------------
  const handleFlightsDragEnd = useCallback(
    async (result) => {
      const { source, destination } = result;
      if (!destination) return;
      if (
        destination.droppableId === source.droppableId &&
        destination.index === source.index
      )
        return;

      // Find the route we’re working in (droppableId === route string)
      const routeIndex = routeData.findIndex(
        (r) => r.route === source.droppableId
      );
      if (routeIndex === -1) return;

      // Re-order flights immutably
      const newRouteData = [...routeData];
      const newFlights = [...newRouteData[routeIndex].flights];
      const [moved] = newFlights.splice(source.index, 1);
      newFlights.splice(destination.index, 0, moved);
      newRouteData[routeIndex] = {
        ...newRouteData[routeIndex],
        flights: newFlights,
      };

      // Optimistic UI
      setRouteData(newRouteData);
      setFlights?.(newRouteData);

      try {
        await axiosInstance.put(`/bucket-list/${tripId}/update/flight`, {
          flight_info: newFlights,
          route: newRouteData[routeIndex].route,
        });
      } catch (err) {
        console.error(err);
        toast({
          title: "Could not save flight order.",
          description: "Please try again.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
        // Roll back
        setRouteData(routeData);
        setFlights?.(routeData);
      }
    },
    [routeData, tripId, setFlights, toast]
  );

  // 2. Delete a flight inside a given route
  const handleDeleteFlight = useCallback(
    async (routeName, index) => {
      const routeIndex = routeData.findIndex((r) => r.route === routeName);
      if (routeIndex === -1) return;

      const newRouteData = [...routeData];
      const newFlights = [...newRouteData[routeIndex].flights];
      newFlights.splice(index, 1); // remove the flight

      newRouteData[routeIndex] = {
        ...newRouteData[routeIndex],
        flights: newFlights,
      };

      // optimistic UI
      setRouteData(newRouteData);
      setFlights?.(newRouteData);

      try {
        await axiosInstance.put(`/bucket-list/${tripId}/update/flight`, {
          flight_info: newFlights, // list WITHOUT the deleted one
          route: routeName,
        });
        handleFlightDelete?.(routeName, index); // notify parent (if any)
      } catch (err) {
        console.error(err);
        toast({
          title: "Could not delete flight.",
          description: "Please try again.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
        // rollback
        setRouteData(routeData);
        setFlights?.(routeData);
      }
    },
    [routeData, tripId, setFlights, toast, handleFlightDelete]
  );

  // ------------------------------------------------------------------------
  const handleCloseDetail = () => {
    setView(null);
  };

  return (
    <Box
      bg={colorMode === "dark" ? "gray.800" : "blackAlpha.50"}
      w="100%"
      h="100vh"
      overflowY="hidden"
    >
      <HStack justifyContent="space-between" bg="white">
        <Box
          flex="1"
          display="flex"
          justifyContent="left"
          p={4}
          pt={2}
          pb={2}
          pl={4}
          maxH="8vh"
          borderBottom="1px"
          borderLeft="1px"
          borderColor="blackAlpha.200"
        >
          <Text
            fontSize="md"
            fontWeight="bold"
            mt={1}
            pb={2}
            color="blackAlpha.700"
          >
            <Translate text="Update Flights" />
          </Text>
          <Spacer />
          <Menu>
            <MenuButton
              as={Button}
              bg="#1A202C"
              color="white"
              _hover={{ bg: "#1A202C" }}
              _active={{ bg: "#1A202C" }}
              size={{ base: "xs", md: "sm", lg: "sm" }}
              rightIcon={<ChevronDownIcon />}
              mr={4}
            >
              <Translate text="Update Flights" />
            </MenuButton>
            <MenuList>
              <MenuItem
                onClick={() => setIsAddFlightOpen(true)}
                fontSize={{ base: "xs", md: "sm", lg: "sm" }}
              >
                <Translate text="Add Bucket List Items" />
              </MenuItem>
              <MenuItem
                onClick={() =>
                  handleSuggestionClick(
                    `Could you give me more recommendation for flightsin ${city}?`,
                    "transportation_conversation"
                  )
                }
                fontSize={{ base: "xs", md: "sm", lg: "sm" }}
              >
                <Translate text="Get more Recommendations" />
              </MenuItem>
            </MenuList>
          </Menu>

          <IconButton
            icon={<CloseIcon boxSize={2.5} />}
            onClick={handleCloseDetail}
            colorScheme="gray"
            size="sm"
            isRound={true}
            position="relative"
            zIndex="tooltip"
            bg="white"
            boxShadow="md"
          />
        </Box>
      </HStack>

      {/* Flight lists */}
      <Box h={{ base: "91vh", md: "89vh", lg: "90vh" }} overflowY="auto">
        <DragDropContext onDragEnd={handleFlightsDragEnd}>
          {routeData.map((routeObj) => (
            <Box key={routeObj.route} mb={6}>
              <Droppable droppableId={routeObj.route}>
                {(provided) => (
                  <VStack
                    spacing={4}
                    h="100%"
                    overflowY="auto"
                    sx={{
                      "&::-webkit-scrollbar": {
                        display: "none",
                      },
                      msOverflowStyle: "none",
                      scrollbarWidth: "none",
                    }}
                    bg="white"
                    align="stretch"
                    p={4}
                    m={2}
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    <Text fontWeight="bold" fontSize="lg" mb={2}>
                      {formatRoute(routeObj.route)}
                    </Text>

                    {routeObj.flights.map((flight, index) => {
                      const convertedPrice = flight.price
                        ? convertCurrency(flight.price, "USD")
                        : null;

                      return (
                        <Draggable
                          key={`${flight.flight_number}-${index}`}
                          draggableId={`${flight.flight_number}-${index}`}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <Box
                              position="relative"
                              bg="white"
                              borderRadius="lg"
                              boxShadow="md"
                              border="1px solid"
                              borderColor="gray.200"
                              display="flex"
                              flexDirection="row"
                              alignItems="center"
                              justifyContent="space-between"
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              onMouseEnter={() =>
                                setHoveredItem(`${routeObj.route}-${index}`)
                              }
                              onMouseLeave={() => setHoveredItem(null)}
                            >
                              <Box
                                position="absolute"
                                top="-10px"
                                left="-10px"
                                bg="black"
                                color="white"
                                borderRadius="full"
                                width="24px"
                                height="24px"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                fontSize="12px"
                                fontWeight="bold"
                              >
                                {index + 1}
                              </Box>
                              <Box
                                {...provided.dragHandleProps}
                                bg="gray.100"
                                alignContent="center"
                                visibility={
                                  hoveredItem === `${routeObj.route}-${index}`
                                    ? "visible"
                                    : "hidden"
                                }
                                minH="184px"
                                h="100%"
                              >
                                <Image
                                  src={GridIcon}
                                  alt="Drag Handle"
                                  boxSize="4"
                                  opacity={
                                    hoveredItem === `${routeObj.route}-${index}`
                                      ? 1
                                      : 0.5
                                  }
                                />
                              </Box>
                              <VStack ml={1} w="full" p={4} pl={0} h="100%">
                                <HStack
                                  align="center"
                                  spacing={4}
                                  w="100%"
                                  mb={4}
                                >
                                  <VStack
                                    align="flex-start"
                                    spacing={1}
                                    flex="1"
                                    h="100%"
                                  >
                                    <Text
                                      fontSize="18px"
                                      fontWeight="bold"
                                      mb={0}
                                    >
                                      {flight.airline}
                                    </Text>
                                    <Text
                                      fontSize="14px"
                                      color="gray.600"
                                      mb={0}
                                    >
                                      {flight.origin_airport} ➔{" "}
                                      {flight.destination_airport}
                                    </Text>
                                    <Text
                                      fontSize="14px"
                                      color="gray.600"
                                      mb={0}
                                    >
                                      <Text
                                        as="span"
                                        fontWeight="bold"
                                        display="inline"
                                        mr={2}
                                      >
                                        <Translate text="Departure:" />
                                      </Text>
                                      <Translate text="From" /> {flight.origin}{" "}
                                      <Translate text="at" />{" "}
                                      {new Date(
                                        flight.departure_at
                                      ).toLocaleDateString()}
                                    </Text>
                                    <Text
                                      fontSize="14px"
                                      color="gray.600"
                                      mb={0}
                                    >
                                      <Text
                                        as="span"
                                        fontWeight="bold"
                                        display="inline"
                                        mr={2}
                                      >
                                        <Translate text="Return:" />
                                      </Text>
                                      <Translate text="From" />{" "}
                                      {flight.destination}{" "}
                                      <Translate text="at" />{" "}
                                      {new Date(
                                        flight.return_at
                                      ).toLocaleDateString()}
                                    </Text>
                                  </VStack>
                                  <Box h="100%" alignItems="top">
                                    <ExternalLinkButton
                                      as="a"
                                      href={flight.link}
                                      rel="noopener noreferrer"
                                      size="sm"
                                      bg="#286328"
                                      _hover="none"
                                      _active="none"
                                      textColor="white"
                                    >
                                      <Translate text="Open" />
                                    </ExternalLinkButton>
                                  </Box>
                                </HStack>
                                <HStack spacing={1} w="100%">
                                  <FaClock color="gray.500" />
                                  <Text
                                    fontSize="sm"
                                    color="gray.500"
                                    ml={1}
                                    mb={0}
                                  >
                                    {Math.floor(flight.duration_to / 60)}h{" "}
                                    {flight.duration_to % 60}m
                                  </Text>
                                  <Text fontSize="sm" color="gray.500" mb={0}>
                                    •
                                  </Text>
                                  <Text fontSize="sm" color="gray.500" mb={0}>
                                    {flight.transfers > 0
                                      ? `${flight.transfers} stop`
                                      : "Non-stop"}
                                  </Text>
                                  <Spacer />
                                  <Text
                                    fontSize="md"
                                    fontWeight="bold"
                                    mb={0}
                                    color="black"
                                  >
                                    {currency}{" "}
                                    {convertedPrice
                                      ? convertedPrice
                                      : flight.price}
                                  </Text>
                                  <Spacer />
                                  <IconButton
                                    icon={<FaTrash />}
                                    aria-label="Delete flight"
                                    borderRadius="full"
                                    size="xs"
                                    visibility={
                                      hoveredItem ===
                                      `${routeObj.route}-${index}`
                                        ? "visible"
                                        : "hidden"
                                    }
                                    onClick={() =>
                                      handleDeleteFlight(routeObj.route, index)
                                    }
                                  />
                                </HStack>
                              </VStack>
                            </Box>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </VStack>
                )}
              </Droppable>
            </Box>
          ))}
        </DragDropContext>
      </Box>

      {/* Add-flight modal (if you use it) */}
      <AddFlightItemModal
        isOpen={isAddFlightOpen}
        onClose={() => setIsAddFlightOpen(false)}
        tripId={tripId}
        setRouteData={setRouteData}
        setFlights={setFlights}
        routes={flights}
      />
    </Box>
  );
};

export default FlightsList;
